export type InboundChangeActionType = "ASSIGN_DOOR" | "ARRIVE_LOAD";

export interface InboundChangeLogEntry {
  id: number;
  manifestNumber: number;
  /** Destination terminal */
  terminalNumber: number;
  actionType: InboundChangeActionType;
  /** DOOR | TRAILER | ARRIVE_LOAD | SEAL_CONDITION | ... */
  fieldName: string;
  previousValue?: string | null;
  newValue?: string | null;
  reasonCode?: string | null;
  reasonDescription?: string | null;
  /** ISO datetime */
  changedAt: string;
  modifiedBy: string;
}

export interface InboundChangeLogFilterParams {
  manifestNumber?: number;
  terminalNumber?: number;
  actionType?: InboundChangeActionType;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  page?: number;
  size?: number;
}

const EMPTY_VALUE = "—";

export const formatDoorLabel = (doorNumber?: string | null): string => {
  if (doorNumber == null || String(doorNumber).trim() === "") return EMPTY_VALUE;
  const normalized = String(doorNumber).trim().toUpperCase();
  if (normalized === "—" || normalized === "-") return EMPTY_VALUE;
  return normalized.startsWith("D-") ? normalized : `D-${normalized}`;
};

export const formatActionLabel = (actionType?: string | null): string => {
  switch (actionType) {
    case "ASSIGN_DOOR":
      return "Assign Door";
    case "ARRIVE_LOAD":
      return "Arrive Load";
    default:
      return actionType || EMPTY_VALUE;
  }
};

export const formatFieldLabel = (fieldName?: string | null): string => {
  switch ((fieldName || "").toUpperCase()) {
    case "DOOR":
      return "Door";
    case "TRAILER":
      return "Trailer";
    case "ARRIVE_LOAD":
      return "Arrive Load";
    case "SEAL_CONDITION":
      return "Seal Condition";
    default:
      return fieldName || EMPTY_VALUE;
  }
};

const displayValue = (value?: string | null): string => {
  if (value == null || String(value).trim() === "") return EMPTY_VALUE;
  return String(value);
};

const reasonText = (entry: InboundChangeLogEntry): string | null => {
  const reason = entry.reasonDescription || entry.reasonCode;
  return reason ? String(reason) : null;
};

const appendReason = (text: string, reason: string | null): string => {
  if (!reason || /reason:/i.test(text)) return text;
  return `${text}, Reason: ${reason}`;
};

const formatArriveDetails = (entry: InboundChangeLogEntry): string => {
  const newValue = displayValue(entry.newValue);
  let body: string;

  if (newValue !== EMPTY_VALUE && /seal/i.test(newValue)) {
    body = newValue;
  } else if (newValue !== EMPTY_VALUE) {
    body = `Seal: ${newValue}`;
  } else {
    body = EMPTY_VALUE;
  }

  return appendReason(body, reasonText(entry));
};

/**
 * Single merged Details cell (not Field / Previous / New / Reason columns).
 * Reason is stored separately on the entry and appended here for display.
 */
export const formatDetailsColumn = (entry: InboundChangeLogEntry): string => {
  const field = (entry.fieldName || "").toUpperCase();
  const isArriveEvent =
    entry.actionType === "ARRIVE_LOAD" ||
    field === "ARRIVE_LOAD" ||
    field === "SEAL_CONDITION";

  if (isArriveEvent) {
    return formatArriveDetails(entry);
  }

  const label = formatFieldLabel(field);
  const previous =
    field === "DOOR" ? formatDoorLabel(entry.previousValue) : displayValue(entry.previousValue);
  const next = field === "DOOR" ? formatDoorLabel(entry.newValue) : displayValue(entry.newValue);

  return appendReason(`${label}: ${previous} → ${next}`, reasonText(entry));
};
